import { Injectable, EnvironmentInjector, inject, runInInjectionContext } from '@angular/core';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { VawtNote } from './vawt-tracker.model';

export interface VawtProgress {
  tasks: { [taskId: string]: { complete: boolean } };
  notes: { [phaseId: string]: VawtNote[] };
}

@Injectable({ providedIn: 'root' })
export class VawtTrackerService {
  private injector = inject(EnvironmentInjector);

  constructor(private db: AngularFireDatabase) {}

  private inCtx<T>(fn: () => T): T {
    return runInInjectionContext(this.injector, fn);
  }

  loadProgress(userId: string): Observable<VawtProgress> {
    const tasks$ = this.inCtx(() => this.db
      .object<{ [taskId: string]: { complete: boolean } }>(`vawt-tracker/${userId}/tasks`)
      .valueChanges()
      .pipe(map(v => v || {})));

    const notes$ = this.inCtx(() => this.db
      .object<{ [phaseId: string]: { [key: string]: VawtNote } }>(`vawt-tracker/${userId}/notes`)
      .valueChanges()
      .pipe(
        map(v => {
          if (!v) return {};
          const result: { [phaseId: string]: VawtNote[] } = {};
          for (const phaseId of Object.keys(v)) {
            result[phaseId] = Object.values(v[phaseId]).sort((a, b) => a.timestamp - b.timestamp);
          }
          return result;
        })
      ));

    return combineLatest([tasks$, notes$]).pipe(
      map(([tasks, notes]) => ({ tasks, notes }))
    );
  }

  setTaskComplete(userId: string, taskId: string, complete: boolean): Promise<void> {
    return this.inCtx(() => this.db.object(`vawt-tracker/${userId}/tasks/${taskId}`).set({ complete }));
  }

  addNote(userId: string, phaseId: string, note: VawtNote): any {
    return this.inCtx(() => this.db.list(`vawt-tracker/${userId}/notes/${phaseId}`).push(note));
  }
}
